import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Alert, Link, MenuItem, TextField, CircularProgress } from '@mui/material';
import TotpCodeField, { isCompleteCode } from './TotpCodeField';

/**
 * The code-entry half of login, shown once /login answers 428.
 *
 * Split out of Login.js rather than inlined as a second branch, mainly so one
 * property holds structurally instead of by discipline: **a rejected code
 * cannot reach Login's page-level error state.** The inline error lives here,
 * in the component that owns the request, so there is no code path by which a
 * wrong code bounces the user back to the password form and makes them start
 * over — you would have to add one deliberately.
 *
 * The parent keeps the mfa_token and does the network call, since it also owns
 * what happens after a successful one. This component owns only what the user
 * is typing, whether that was rejected, and the clock.
 */

const formatRemaining = (ms) => {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const factorLabel = (factor) => (factor.type ? factor.type.toUpperCase() : factor.id);

function MfaCodeStep({ factors, factorId, onFactorChange, expiresAt, onSubmit, onStartOver }) {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const hasDeadline = Number.isFinite(expiresAt);

  useEffect(() => {
    if (!hasDeadline) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [hasDeadline]);

  const remainingMs = hasDeadline ? Math.max(0, expiresAt - now) : null;
  const expired = hasDeadline && remainingMs <= 0;

  const handleCodeChange = (next) => {
    setCode(next);
    setCodeError(null);
  };

  const handleFactorChange = (event) => {
    // The code is specific to the factor it was generated for, so switching
    // invalidates whatever is already typed.
    setCode('');
    setCodeError(null);
    onFactorChange(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isCompleteCode(code) || submitting || expired) return;

    setSubmitting(true);
    setCodeError(null);

    try {
      const result = await onSubmit(code);
      // On success the parent navigates or tears this down, so submitting is
      // deliberately left true — the button stays disabled through the
      // transition rather than flickering back to active.
      if (!result || !result.ok) {
        setCodeError((result && result.message) || 'Something went wrong. Please try again.');
        setSubmitting(false);
      }
    } catch {
      setCodeError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  // Held shut while a code is in flight: starting over tears this component
  // down, and the reply then lands on a parent that has already gone back to
  // the password form — a correct code would finish the login and navigate out
  // from under the form the user is now looking at. The exception is the
  // expired panel below, where this link is the only control there is and the
  // request still out was sent against a window that has since closed; Login
  // ignores a reply to an abandoned challenge, so it cannot do any harm there.
  const startOver = (
    <Link
      component="button"
      type="button"
      variant="body2"
      onClick={onStartOver}
      disabled={submitting && !expired}
      sx={{ textDecoration: 'none' }}
    >
      Start over
    </Link>
  );

  if (expired) {
    return (
      <Box sx={{ mt: 1, width: '100%' }}>
        <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
          This sign-in request has expired. Start over to try again.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>{startOver}</Box>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the 6-digit code from your authenticator app to finish signing in.
      </Typography>

      {factors.length > 1 && (
        <TextField
          select
          fullWidth
          margin="normal"
          id="mfa-factor"
          label="Verification method"
          value={factorId}
          onChange={handleFactorChange}
          disabled={submitting}
        >
          {factors.map((factor) => (
            <MenuItem key={factor.id} value={factor.id}>
              {factorLabel(factor)}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TotpCodeField
        id="mfa-code"
        label="Authentication code"
        value={code}
        onChange={handleCodeChange}
        errorText={codeError}
        helperText={remainingMs === null ? undefined : `Expires in ${formatRemaining(remainingMs)}`}
        disabled={submitting}
        autoFocus
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        // The label is fixed rather than derived from the content, which is
        // replaced by a spinner while the request is in flight — without this
        // the button loses its accessible name at exactly the moment a screen
        // reader user is waiting to hear what it is doing.
        aria-label="Verify"
        sx={{ mt: 3, mb: 2, py: 1.5 }}
        disabled={submitting || !isCompleteCode(code)}
      >
        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>{startOver}</Box>
    </Box>
  );
}

export default MfaCodeStep;
